import { Login } from '@/model/user';
import { Role } from '@/model/role';
import { useUsersStore } from '@/store/users-store';
import i18n from '@/config/i18n';
import { RouteLocationRaw, useRoute, useRouter } from 'vue-router';
import { CalendarResourceType } from '@/store/calendar-store';
import { useAppLoad } from '@/composables/app-load';

const CALENDAR_HOME: RouteLocationRaw = {
  name: 'Calendar',
  query: { viewType: CalendarResourceType.EMPLOYEE },
};

export function homeRoute(): RouteLocationRaw {
  return useUsersStore().isStaffOrMore ? CALENDAR_HOME : { name: 'MyAccount' };
}

export function useAuth() {
  const usersStore = useUsersStore();
  const route = useRoute();
  const router = useRouter();
  const appLoad = useAppLoad();

  async function loginAndRedirect(loginDto: Login) {
    await usersStore.login(loginDto);
    if (!usersStore.isLoggedIn) {
      return;
    }
    await usersStore.setLanguage(i18n.global.locale.value);
    if (mustChooseSpace()) {
      await router.replace({ name: 'Spaces', query: route.query });
      return;
    }
    await loadSpaceAndRedirect();
  }

  async function chooseSpace(vendorId: number | null) {
    await usersStore.enterSpace(vendorId);
    await loadSpaceAndRedirect();
  }

  async function unlockSession(userId: number, pin: string): Promise<boolean> {
    if (!(await usersStore.unlockSession(userId, pin))) {
      return false;
    }
    await loadSpaceAndRedirect();

    return true;
  }

  async function joinVendor(vendorId: number) {
    await usersStore.enterSpace(vendorId);
    await loadSpaceAndRedirect();
  }

  async function followRequestedRedirect(fallback: RouteLocationRaw) {
    await router.replace(requestedRedirect() ?? fallback);
  }

  function mustChooseSpace(): boolean {
    if (usersStore.loggedInVendorId || !usersStore.user.spaces.vendors.length) {
      return false;
    }
    const target = requestedRedirect();

    return !target || router.resolve(target).meta.role >= Role.STAFF;
  }

  function requestedRedirect(): RouteLocationRaw | null {
    const { redirect, ...query } = route.query;
    if (!redirect || redirect === 'Calendar') {
      return null;
    }

    return { name: redirect as string, query };
  }

  async function loadSpaceAndRedirect() {
    await usersStore.getUser(usersStore.loggedInUserId);
    await appLoad.loadManagerData();
    await followRequestedRedirect(homeRoute());
  }

  return {
    loginAndRedirect,
    chooseSpace,
    joinVendor,
    unlockSession,
    followRequestedRedirect,
  };
}
